import * as React from 'react';
import { clsx } from 'clsx';

export interface RadioProps {
  /**
   * The label for the radio button
   */
  label?: string;
  /**
   * Whether the radio is selected
   */
  checked?: boolean;
  /**
   * Callback when selection changes
   */
  onChange?: (checked: boolean) => void;
  /**
   * Whether the radio is disabled
   */
  disabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
  /**
   * Name attribute for form submission (groups radios together)
   */
  name?: string;
  /**
   * Value attribute for form submission
   */
  value?: string;
}

const radioStyles: React.CSSProperties = {
  width: '16px',
  height: '16px',
  border: '1px solid #7e7e7e', // grey-500
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  flexShrink: 0,
  backgroundColor: '#ffffff',
};

const checkedStyles: React.CSSProperties = {
  border: '1px solid #0e8a0e', // brand-600
};

const checkedHoverStyles: React.CSSProperties = {
  border: '1px solid #005700', // brand-800
};

const uncheckedHoverStyles: React.CSSProperties = {
  border: '1px solid #2f2f2f', // grey-900
};

const innerDotStyles: React.CSSProperties = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#0e8a0e', // brand-600
  transition: 'all 0.2s ease-in-out',
};

const innerDotHoverStyles: React.CSSProperties = {
  backgroundColor: '#005700', // brand-800
};

const labelStyles: React.CSSProperties = {
  fontSize: '13px',
  color: '#2f2f2f',
  cursor: 'pointer',
  userSelect: 'none' as const,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const wrapperStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

/**
 * Radio component - Arbor Design System
 *
 * A radio button input with label support.
 */
export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      checked = false,
      onChange,
      disabled = false,
      className,
      style,
      'data-testid': dataTestId,
      name,
      value,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const radioId = React.useId();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange?.(e.target.checked);
      }
    };

    const circleStyle: React.CSSProperties = {
      ...radioStyles,
      ...(checked && !disabled && checkedStyles),
      ...(isHovered && !disabled && !checked && uncheckedHoverStyles),
      ...(isHovered && !disabled && checked && checkedHoverStyles),
      ...(disabled && !checked && { backgroundColor: '#efefef', border: '1px solid #7e7e7e' }),
      ...(disabled && checked && { backgroundColor: '#efefef', border: '1px solid #7e7e7e' }),
      ...(isFocused && !disabled && {
        boxShadow: '0px 0px 0px 3px #3cad51',
      }),
    };

    const dotStyle: React.CSSProperties = {
      ...innerDotStyles,
      ...(isHovered && !disabled && innerDotHoverStyles),
      ...(disabled && { backgroundColor: '#7e7e7e' }),
    };

    return (
      <div
        className={clsx('arbor-radio-wrapper', className)}
        style={{ ...wrapperStyles, ...style }}
        data-testid={dataTestId}
      >
        <input
          ref={ref}
          id={radioId}
          type="radio"
          checked={checked}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          name={name}
          value={value}
          style={{
            position: 'absolute',
            opacity: 0,
            width: 0,
            height: 0,
          }}
          aria-checked={checked}
        />
        <label
          htmlFor={radioId}
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => !disabled && setIsHovered(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <div style={circleStyle}>
            {checked && <div style={dotStyle} />}
          </div>
          {label && (
            <span
              style={{
                ...labelStyles,
                color: disabled ? '#7e7e7e' : '#2f2f2f',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {label}
            </span>
          )}
        </label>
      </div>
    );
  }
);

Radio.displayName = 'Radio';
